import React, { useEffect, useState } from 'react';
import {
  Smartphone, Activity, LayoutDashboard, CalendarCheck, Brain, FileText, Printer, Compass,
  Lock, MessageCircle, TrendingUp, AlertCircle, Sparkles, Crown, ArrowRight, Clock, Zap, Gauge,
} from 'lucide-react';
import { trackEvent } from './utils/analytics';

const RED = '#DC2626';
const ORANGE = '#E0691F';
const DARK_GREEN = '#3D5A35';

const useColumns = () => {
  const getCols = () => (window.innerWidth <= 768 ? 1 : window.innerWidth <= 1060 ? 2 : 3);
  const [cols, setCols] = useState(getCols);
  useEffect(() => {
    const onResize = () => setCols(getCols());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return cols;
};

const Eyebrow = ({ color, children }) => (
  <div style={{ fontSize: '12px', fontWeight: '700', letterSpacing: '1.4px', textTransform: 'uppercase', color }}>
    {children}
  </div>
);

const Chip = ({ children, color = '#555', bg = '#F3F4F1', border = '#E5E7E1' }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: '4px 10px', borderRadius: '8px',
    fontSize: '12px', fontWeight: '600', color, background: bg, border: `1px solid ${border}`, whiteSpace: 'nowrap',
  }}>
    {children}
  </span>
);

const Bar = ({ value, color, track = '#EEF0EA', marker }) => (
  <div style={{ position: 'relative', height: '7px', borderRadius: '4px', background: track }}>
    <div style={{ width: `${value}%`, height: '100%', borderRadius: '4px', background: color }} />
    {marker && (
      <div style={{ position: 'absolute', left: `${marker}%`, top: '-4px', width: '2px', height: '15px', background: '#444' }} />
    )}
  </div>
);

const currentWeekLabel = () => {
  const monday = new Date();
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const format = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `Week of ${format(monday)} – ${format(sunday)}`;
};

const ACCURACY_TREND = [52, 55, 54, 58, 57, 60, 67];

const AccuracyTrend = ({ appColors }) => {
  const width = 300;
  const height = 44;
  const min = 45;
  const max = 72;
  const points = ACCURACY_TREND.map((value, idx) => [
    (idx / (ACCURACY_TREND.length - 1)) * (width - 8) + 4,
    height - 4 - ((value - min) / (max - min)) * (height - 8),
  ]);
  const [lastX, lastY] = points[points.length - 1];
  return (
    <div style={{ background: '#FFFBF0', border: '1px solid #F3E4BC', borderRadius: '12px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '16px' }}>
      <div style={{ flexShrink: 0 }}>
        <div style={{ fontSize: '11px', color: appColors.textSecondary }}>Accuracy, last 7 weeks</div>
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#C98A1B' }}>67% <span style={{ fontSize: '12px', color: appColors.primary }}>▲ 7</span></div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ flex: 1, width: 0, height: `${height}px`, minWidth: 0 }}>
        <polyline points={points.map((point) => point.join(',')).join(' ')} fill="none" stroke="#D9A53B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        <circle cx={lastX} cy={lastY} r="4" fill="#D9A53B" />
      </svg>
    </div>
  );
};

// Mini, non-interactive previews of the real dashboard sections
const WeeklyPreview = ({ appColors, compact }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
      <img src="/animals/hippo.png" alt="" style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#F7E7BE', objectFit: 'contain', flexShrink: 0 }} />
      <div>
        <Eyebrow color={appColors.primary}>{currentWeekLabel()}</Eyebrow>
        <div style={{ fontSize: '15px', fontWeight: '700', color: appColors.textPrimary, lineHeight: 1.3 }}>
          A busy week (accuracy up 7 points) – and 2 skills need your help.
        </div>
      </div>
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      <Chip><Activity size={12} />10 sessions</Chip>
      <Chip><Clock size={12} />48 minutes</Chip>
      <Chip><Sparkles size={12} />701 XP</Chip>
      <Chip color={appColors.primary} bg="#EEF5E6" border="#CFE3B8"><TrendingUp size={12} />Word Problems climbing</Chip>
      <Chip color={RED} bg="#FEF2F2" border="#FBD5D5"><AlertCircle size={12} />Subtraction slipped to 33%</Chip>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '1fr 1fr', gap: '10px' }}>
      {[
        { skill: 'Addition', pct: 67, target: 85, detail: 'regrouping in tens place' },
        { skill: 'Subtraction', pct: 33, target: 80, detail: 'borrowing across zeros' },
      ].map(({ skill, pct, target, detail }) => (
        <div key={skill} style={{ background: '#FEF6F6', border: '1px solid #FBD5D5', borderRadius: '12px', padding: '10px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: '700', marginBottom: '8px' }}>
            <span style={{ color: appColors.textPrimary }}>{skill}</span>
            <span style={{ color: RED }}>{pct}%</span>
          </div>
          <Bar value={pct} color="#E5484D" track="#FBDADA" marker={target} />
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', fontSize: '11px', marginTop: '8px' }}>
            <span style={{ color: RED, fontWeight: '600' }}>{detail}</span>
            <span style={{ color: appColors.textSecondary, whiteSpace: 'nowrap' }}>Target: {target}%</span>
          </div>
        </div>
      ))}
    </div>
    <AccuracyTrend appColors={appColors} />
  </div>
);

const TonightPreview = ({ appColors }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    {['Look at the subtraction breakdown', 'Print this week\'s practice sheet', 'Ask the dinner-table question'].map((task) => (
      <div key={task} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#FDF6E7', borderRadius: '10px', padding: '9px 12px', fontSize: '13px', fontWeight: '600', color: appColors.textPrimary }}>
        <span style={{ width: '16px', height: '16px', borderRadius: '5px', border: `2px solid ${appColors.accent}`, flexShrink: 0 }} />
        {task}
      </div>
    ))}
    <div style={{ fontSize: '12px', fontWeight: '700', color: appColors.primary, margin: '2px 0 4px' }}>0 of 3 done – about 6 min left</div>
    <div style={{ background: DARK_GREEN, borderRadius: '12px', padding: '12px 14px', marginTop: '4px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: appColors.accent, fontSize: '10px', fontWeight: '700', letterSpacing: '1px' }}>
        <MessageCircle size={12} />DINNER-TABLE QUESTION
      </div>
      <div style={{ color: 'white', fontSize: '14px', fontWeight: '700', marginTop: '6px', lineHeight: 1.35 }}>
        "3 spoons and 4 forks – how many utensils in total?"
      </div>
    </div>
  </div>
);

const BehaviourPreview = ({ appColors }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
    {[
      { label: 'Focused', value: 98, color: '#5E983B' },
      { label: 'Steady', value: 100, color: ORANGE },
      { label: 'Rushed', value: 92, color: '#F59E0B' },
    ].map((row) => (
      <div key={row.label}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: '700', marginBottom: '5px' }}>
          <span style={{ color: appColors.textPrimary }}>{row.label}</span>
          <span style={{ color: row.color }}>{row.value}%</span>
        </div>
        <Bar value={row.value} color={row.color} />
      </div>
    ))}
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', gap: '8px', height: '78px', background: '#FBF8F1', borderRadius: '10px', padding: '8px 10px 6px', marginTop: '2px' }}>
      {[
        { label: 'Morning', value: 45 },
        { label: 'Afternoon', value: 100 },
        { label: 'Evening', value: 70 },
      ].map((slot) => (
        <div key={slot.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <div style={{ width: '22px', height: `${slot.value * 0.45}px`, borderRadius: '5px 5px 2px 2px', background: slot.value === 100 ? ORANGE : '#E8D9B5' }} />
          <span style={{ fontSize: '10px', fontWeight: '600', color: appColors.textSecondary }}>{slot.label}</span>
        </div>
      ))}
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
      <Chip><Clock size={12} />Best time of day</Chip>
      <Chip><Gauge size={12} />Confidence gap</Chip>
      <Chip><Zap size={12} />Distraction meter</Chip>
      <Chip>Thinking sweet spot</Chip>
    </div>
  </div>
);

const ReportPreview = ({ appColors }) => (
  <div style={{ position: 'relative', background: 'white', border: '1px solid #ECE6D6', borderRadius: '12px', padding: '12px 14px' }}>
    <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
      <Chip color="#B7791F" bg="#FDF3DC" border="#F3DFA8"><Crown size={11} />PRO</Chip>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '700', color: appColors.textPrimary, marginBottom: '10px' }}>
      <FileText size={15} color={appColors.primary} />AI Report.pdf
    </div>
    {[
      { title: 'Strengths', sub: 'What\'s already working, by topic', icon: TrendingUp },
      { title: 'Where help is needed', sub: 'Question types that trip them up', icon: AlertCircle },
      { title: '7-day plan', sub: 'Ten minutes an evening', icon: CalendarCheck },
      { title: 'Teacher summary', sub: 'One shareable page for school', icon: FileText },
    ].map(({ title, sub, icon: Icon }) => (
      <div key={title} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '6px 0', borderTop: '1px dashed #EEE7D4' }}>
        <Icon size={14} color={appColors.primary} style={{ marginTop: '2px', flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: '12px', fontWeight: '700', color: appColors.textPrimary }}>{title}</div>
          <div style={{ fontSize: '11px', color: appColors.textSecondary }}>{sub}</div>
        </div>
      </div>
    ))}
    <div style={{ marginTop: '8px', paddingTop: '10px', borderTop: '1px solid #EEE7D4' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: appColors.textSecondary, marginBottom: '5px' }}>
        <span>Reports today</span><span>0 of 3 used</span>
      </div>
      <Bar value={4} color={appColors.primary} />
      <div style={{ marginTop: '10px', textAlign: 'center', background: appColors.primary, color: 'white', borderRadius: '9px', padding: '8px', fontSize: '12px', fontWeight: '700' }}>
        Generate New Report
      </div>
    </div>
  </div>
);

const PracticePreview = ({ appColors }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
    <div style={{ border: `2px solid ${appColors.separatorBrown}`, borderRadius: '12px', overflow: 'hidden', background: 'white' }}>
      <div style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#EEF0EA', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', color: appColors.primary }}>5</span>
          <Chip color="#C98A1B" bg="#FDF3DC" border="#F6E3B5">Word Problems</Chip>
        </div>
        <div style={{ fontSize: '14px', fontWeight: '700', color: appColors.textPrimary, textAlign: 'center', lineHeight: 1.35 }}>
          Mia saw 4 blue birds and 2 red birds. How many birds did Mia see in all?
        </div>
      </div>
      <div style={{ borderTop: `2px dashed ${appColors.separatorBrown}`, background: '#F8F6F0', padding: '10px 14px 12px' }}>
        <div style={{ fontSize: '11px', fontWeight: '600', color: appColors.textSecondary, marginBottom: '4px' }}>Your Answer:</div>
        <div style={{ height: '24px', borderRadius: '8px', border: `2px solid ${appColors.separatorBrown}`, background: 'white' }} />
        <div style={{ borderTop: '1px solid #E8E5DC', margin: '10px 0 8px' }} />
        <div style={{ fontSize: '12px', fontWeight: '700', color: appColors.primary }}>✓ Step-by-Step Solutions</div>
        <div style={{ fontSize: '11px', color: '#4B5563', lineHeight: 1.45, margin: '3px 0 6px' }}>
          We need to find the total number of birds by adding the two numbers.
        </div>
        <div style={{ fontSize: '12px', fontWeight: '700', color: appColors.primary }}>💡 Detailed explanations</div>
        {['Find the number of blue birds Mia saw.', 'Add the red birds to the blue birds.', 'The answer is: …'].map((step) => (
          <div key={step} style={{ fontSize: '11px', color: '#4B5563', lineHeight: 1.45, marginTop: '2px' }}>• {step}</div>
        ))}
      </div>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '6px' }}>
      {['EN', 'NL', 'DE', 'ES', 'BG', 'RU'].map((lang) => (
        <Chip key={lang}>{lang}</Chip>
      ))}
    </div>
  </div>
);

const JourneyPreview = ({ appColors, compact }) => {
  const stops = [
    { name: 'Addition', pct: 61 },
    { name: 'Subtraction', pct: 61 },
    { name: 'Word Problems', pct: 64 },
    { name: 'Multiplication' },
    { name: 'Division' },
    { name: 'Fractions' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${compact ? 3 : 6}, 1fr)`, gap: compact ? '14px 8px' : '8px' }}>
      {stops.map((stop) => (
        <div key={stop.name} style={{ textAlign: 'center' }}>
          <div style={{
            width: '52px', height: '52px', margin: '0 auto', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: stop.pct ? '3px solid #D9A53B' : '2px dashed #CBD2DA', background: stop.pct ? '#FFFBF0' : '#F8FAFC',
            fontSize: '14px', fontWeight: '700', color: '#C98A1B',
          }}>
            {stop.pct ? `${stop.pct}%` : <Lock size={16} color="#94A3B8" />}
          </div>
          <div style={{ fontSize: '12px', fontWeight: '700', color: appColors.textPrimary, marginTop: '6px' }}>{stop.name}</div>
          <div style={{ fontSize: '11px', fontWeight: '600', color: stop.pct ? '#C98A1B' : '#94A3B8' }}>{stop.pct ? 'Developing' : 'Locked'}</div>
        </div>
      ))}
    </div>
  );
};

const FEATURES = [
  {
    key: 'weekly', span: 2, icon: CalendarCheck, Preview: WeeklyPreview,
    title: 'This week, in one sentence',
    text: 'A plain-language weekly summary: sessions, time, XP, which skills are climbing and which ones slipped below the target for their grade.',
  },
  {
    key: 'tonight', span: 1, icon: ArrowRight, Preview: TonightPreview,
    title: 'What to do tonight',
    text: 'A short to-do list for parents plus a screen-free dinner-table question that practises the weakest skill.',
  },
  {
    key: 'behaviour', span: 1, icon: Brain, Preview: BehaviourPreview,
    title: 'How your child learns',
    text: 'Every answer is tagged focused, steady or rushed – see when practice works best, confidence gaps and the kind of mistakes made.',
  },
  {
    key: 'report', span: 1, icon: FileText, Preview: ReportPreview,
    title: 'AI report as a PDF',
    text: 'Fresh analysis after every session: strengths, where help is needed, a 7-day plan and a summary you can hand to the teacher.',
  },
  {
    key: 'practice', span: 1, icon: Printer, Preview: PracticePreview,
    title: 'Printable practice sheets',
    text: 'Personalized problems that target weak areas, with step-by-step solutions and answer keys – printed in 6 languages.',
  },
  {
    key: 'journey', span: 3, icon: Compass, Preview: JourneyPreview,
    title: 'The journey so far',
    text: 'The same skill path your child sees in the app – with the parent view of every stop: success rate, skip rate, response time and the next collectable.',
  },
];

const FeatureCard = ({ feature, appColors, cols }) => {
  const { icon: Icon, Preview, title, text, span } = feature;
  const wide = Math.min(span, cols) === 3;
  return (
    <div style={{
      gridColumn: `span ${Math.min(span, cols)}`,
      background: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '24px',
      border: '1px solid rgba(233, 208, 155, 0.6)',
      boxShadow: '0 20px 40px -24px rgba(0, 0, 0, 0.18)',
      padding: '24px',
      display: wide ? 'grid' : 'flex',
      gridTemplateColumns: wide ? 'minmax(240px, 1fr) 2fr' : undefined,
      flexDirection: 'column',
      gap: '20px',
      alignItems: wide ? 'center' : 'stretch',
      boxSizing: 'border-box',
    }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <span style={{ width: '34px', height: '34px', borderRadius: '10px', background: `${appColors.primary}1F`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon size={18} color={appColors.primary} />
          </span>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: appColors.textPrimary }}>{title}</h3>
        </div>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.55, color: appColors.textSecondary }}>{text}</p>
      </div>
      <div style={{ marginTop: wide ? 0 : 'auto' }}>
        <Preview appColors={appColors} compact={cols === 1} />
      </div>
    </div>
  );
};

const PortalOverview = ({ appColors, onParentAccountClick }) => {
  const cols = useColumns();
  const isMobile = cols === 1;

  const steps = [
    { icon: Smartphone, title: 'Your child plays on iPhone', text: 'Short, game-like math sessions in the MathZoo Junior app.' },
    { icon: Activity, title: 'Every answer is analysed', text: 'Accuracy, speed, focus and confidence are tracked automatically.' },
    { icon: LayoutDashboard, title: 'You see the parent view here', text: 'Insights, reports and practice sheets – updated after every session.' },
  ];

  return (
    <section id="portal-overview" style={{ position: 'relative', zIndex: 10, padding: isMobile ? '40px 16px 24px' : '56px 48px 32px' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* How it works */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <Eyebrow color={appColors.primary}>How the portal works</Eyebrow>
          <h2 style={{ fontSize: isMobile ? '26px' : '36px', fontWeight: 'bold', color: appColors.textPrimary, margin: '8px 0 0', lineHeight: 1.2 }}>
            The app is for your child. <span style={{ color: appColors.primary }}>This dashboard is for you.</span>
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '14px', marginBottom: isMobile ? '48px' : '72px' }}>
          {steps.map(({ icon: Icon, title, text }, idx) => (
            <div key={title} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', background: `${appColors.primary}14`, border: `1px solid ${appColors.primary}55`, borderRadius: '18px', padding: '18px' }}>
              <span style={{ position: 'relative', width: '44px', height: '44px', borderRadius: '12px', background: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={22} color={appColors.primary} />
                <span style={{ position: 'absolute', top: '-6px', right: '-6px', width: '20px', height: '20px', borderRadius: '50%', background: appColors.primary, color: 'white', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{idx + 1}</span>
              </span>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: appColors.textPrimary }}>{title}</div>
                <div style={{ fontSize: '13px', color: appColors.textSecondary, marginTop: '3px', lineHeight: 1.5 }}>{text}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Feature overview */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <Eyebrow color={appColors.primary}>What's inside</Eyebrow>
          <h2 style={{ fontSize: isMobile ? '26px' : '36px', fontWeight: 'bold', color: appColors.textPrimary, margin: '8px 0 8px', lineHeight: 1.2 }}>
            Everything you need to help at home
          </h2>
          <p style={{ fontSize: '16px', color: appColors.textSecondary, margin: '0 auto', maxWidth: '620px', lineHeight: 1.6 }}>
            Not just scores. The portal explains <em>why</em> your child gets stuck and gives you a 10-minute plan to fix it.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: '18px' }}>
          {FEATURES.map((feature) => (
            <FeatureCard key={feature.key} feature={feature} appColors={appColors} cols={cols} />
          ))}
        </div>
        <p style={{ textAlign: 'center', fontSize: '12px', color: appColors.textLight, margin: '12px 0 0' }}>
          Previews show sample data.
        </p>

        {/* Closing CTA */}
        <div style={{
          marginTop: isMobile ? '40px' : '56px',
          background: DARK_GREEN,
          borderRadius: '24px',
          padding: isMobile ? '24px' : '32px 40px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: '20px',
        }}>
          <div>
            <div style={{ color: 'white', fontSize: isMobile ? '20px' : '24px', fontWeight: '700' }}>
              Already on Expert in the app?
            </div>
            <div style={{ color: '#D7E4CC', fontSize: '14px', marginTop: '6px', lineHeight: 1.5 }}>
              Expert unlocks your Parent Account – enter it with the same email you use in MathZoo Junior. New here? Download the app and upgrade to Expert first.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexDirection: isMobile ? 'column' : 'row' }}>
            <button
              onClick={onParentAccountClick}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: 'white', color: DARK_GREEN, border: 'none', borderRadius: '12px', padding: '13px 20px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Enter your Parent Account <ArrowRight size={18} />
            </button>
            <a
              href="https://apps.apple.com/us/app/mathzoo-junior/id6757204107"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('app_store_click', { location: 'login_overview_cta' })}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: 'transparent', color: 'white', border: `1.5px solid ${appColors.accent}`, borderRadius: '12px', padding: '12px 20px', fontSize: '15px', fontWeight: '700', textDecoration: 'none', whiteSpace: 'nowrap' }}
            >
              Get the iPhone app
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PortalOverview;
